/**
 * Motion Sensor Calculations & Analytics
 *
 * This module provides comprehensive calculations and analytics for motion sensors.
 * Includes detection counting, activity patterns, timeline generation, and statistics.
 *
 * @module motionCalculations
 * @version 1.0.0
 * @date 2026-01-23
 */

// ====================================
// Types & Interfaces
// ====================================

/**
 * Motion detection event
 */
export interface MotionEvent {
  timestamp: Date;
  duration: number; // Duration in seconds
  intensity?: number; // 0-100 (if available)
  zone?: string; // Detection zone (if multi-zone sensor)
  confidence?: number; // 0-1 (detection confidence)
}

/**
 * Motion pattern data for a specific hour
 */
export interface MotionHourPattern {
  hour: number; // 0-23
  detectionCount: number; // Number of detections in this hour
  totalDuration: number; // Total seconds of motion in this hour
  averageIntensity: number; // Average intensity (0-100)
  isPeakHour: boolean; // Is this a peak activity hour
}

/**
 * Motion timeline item for display
 */
export interface MotionTimelineItem {
  id: string;
  timestamp: Date;
  type: 'motion_start' | 'motion_end' | 'alert' | 'manual_test';
  duration?: number;
  intensity?: number;
  description: string;
  icon: string;
  color: string;
}

/**
 * Motion statistics summary
 */
export interface MotionStats {
  totalDetections: number; // Total detections in period
  activeHours: number; // Hours with motion activity
  totalActiveSeconds: number; // Total seconds of motion
  averageDetectionDuration: number; // Average duration per detection
  longestDetection: number; // Longest single detection (seconds)
  shortestDetection: number; // Shortest detection (seconds)
  peakHour: number; // Hour with most activity (0-23)
  quietestHour: number; // Hour with least activity (0-23)
  averageIntensity: number; // Average intensity (0-100)
  falseAlarmRate: number; // Estimated false alarm rate (0-1)
  confidenceScore: number; // Overall confidence (0-1)
}

/**
 * Motion sensor status
 */
export interface MotionSensorStatus {
  isActive: boolean; // Currently detecting motion
  lastDetection: Date | null; // Last detection time
  timeSinceLastDetection: number; // Minutes since last detection
  detectionCountToday: number; // Detections today
  detectionCountWeek: number; // Detections this week
  detectionCountMonth: number; // Detections this month
  activeHoursToday: number; // Active hours today
  currentStreak: number; // Current consecutive detections
  longestStreak: number; // Longest streak of detections
  batteryLevel?: number; // Battery level (0-100)
  sensitivity: number; // Sensitivity level (0-100)
  coverageArea: number; // Coverage area in square meters
}

// ====================================
// Core Calculations
// ====================================

/**
 * Calculate total detection count for a time period
 *
 * @param events - Array of motion events
 * @param startDate - Start of time period
 * @param endDate - End of time period
 * @returns Number of detections in the period
 *
 * @example
 * const count = calculateDetectionCount(events, startOfDay, endOfDay);
 * // Returns: 47
 */
export function calculateDetectionCount(
  events: MotionEvent[],
  startDate: Date,
  endDate: Date,
): number {
  if (!events || events.length === 0) return 0;

  return events.filter((event) => {
    const eventTime = event.timestamp.getTime();
    return eventTime >= startDate.getTime() && eventTime <= endDate.getTime();
  }).length;
}

/**
 * Calculate active hours (hours with at least one detection)
 *
 * @param events - Array of motion events
 * @param startDate - Start of time period
 * @param endDate - End of time period
 * @returns Number of unique hours with activity
 *
 * @example
 * const hours = getActiveHours(events, startOfDay, endOfDay);
 * // Returns: 6.5
 */
export function getActiveHours(events: MotionEvent[], startDate: Date, endDate: Date): number {
  if (!events || events.length === 0) return 0;

  const filteredEvents = events.filter((event) => {
    const eventTime = event.timestamp.getTime();
    return eventTime >= startDate.getTime() && eventTime <= endDate.getTime();
  });

  // Get unique hours with activity
  const uniqueHours = new Set<string>();
  filteredEvents.forEach((event) => {
    const hourKey = `${event.timestamp.getDate()}-${event.timestamp.getHours()}`;
    uniqueHours.add(hourKey);
  });

  // Calculate partial hours based on total duration
  const totalDurationHours = filteredEvents.reduce((sum, event) => {
    return sum + event.duration / 3600;
  }, 0);

  // Return the greater of unique hours or duration-based hours
  return Math.max(uniqueHours.size, totalDurationHours);
}

/**
 * Calculate estimated false alarm rate
 *
 * False alarms are estimated based on very short detections (<2 seconds)
 * and low confidence scores.
 *
 * @param events - Array of motion events
 * @returns False alarm rate (0-1, where 0 = no false alarms, 1 = all false alarms)
 *
 * @example
 * const rate = calculateFalseAlarmRate(events);
 * // Returns: 0.12 (12% false alarms)
 */
export function calculateFalseAlarmRate(events: MotionEvent[]): number {
  if (!events || events.length === 0) return 0;

  let falseAlarmCount = 0;

  events.forEach((event) => {
    let suspicionScore = 0;

    // Very short detections are suspicious
    if (event.duration < 2) {
      suspicionScore += 0.5;
    }

    // Low confidence is suspicious
    if (event.confidence !== undefined && event.confidence < 0.5) {
      suspicionScore += 0.3;
    }

    // Very low intensity is suspicious
    if (event.intensity !== undefined && event.intensity < 20) {
      suspicionScore += 0.2;
    }

    // If total suspicion > 0.7, count as likely false alarm
    if (suspicionScore > 0.7) {
      falseAlarmCount++;
    }
  });

  return events.length > 0 ? falseAlarmCount / events.length : 0;
}

/**
 * Get motion pattern by hour (24-hour breakdown)
 *
 * @param events - Array of motion events
 * @param date - Date to analyze (defaults to today)
 * @returns Array of 24 hourly patterns
 *
 * @example
 * const pattern = getMotionPattern(events);
 * // Returns: [
 * //   { hour: 0, detectionCount: 0, totalDuration: 0, ... },
 * //   { hour: 1, detectionCount: 0, totalDuration: 0, ... },
 * //   ...
 * //   { hour: 8, detectionCount: 15, totalDuration: 180, isPeakHour: true },
 * //   ...
 * // ]
 */
export function getMotionPattern(
  events: MotionEvent[],
  date: Date = new Date(),
): MotionHourPattern[] {
  // Initialize 24 hours
  const pattern: MotionHourPattern[] = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    detectionCount: 0,
    totalDuration: 0,
    averageIntensity: 0,
    isPeakHour: false,
  }));

  if (!events || events.length === 0) return pattern;

  // Filter events for the specified date
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(date);
  dayEnd.setHours(23, 59, 59, 999);

  const dayEvents = events.filter((event) => {
    const eventTime = event.timestamp.getTime();
    return eventTime >= dayStart.getTime() && eventTime <= dayEnd.getTime();
  });

  // Aggregate data by hour
  dayEvents.forEach((event) => {
    const hour = event.timestamp.getHours();
    pattern[hour].detectionCount++;
    pattern[hour].totalDuration += event.duration;

    if (event.intensity !== undefined) {
      pattern[hour].averageIntensity += event.intensity;
    }
  });

  // Calculate averages
  pattern.forEach((hourData) => {
    if (hourData.detectionCount > 0) {
      hourData.averageIntensity = hourData.averageIntensity / hourData.detectionCount;
    }
  });

  // Find peak hours (top 3 hours with most detections)
  const sortedPattern = [...pattern].sort((a, b) => b.detectionCount - a.detectionCount);
  const peakThreshold = sortedPattern[2]?.detectionCount || 0;

  pattern.forEach((hourData) => {
    hourData.isPeakHour = hourData.detectionCount >= peakThreshold && hourData.detectionCount > 0;
  });

  return pattern;
}

/**
 * Generate motion timeline for display
 *
 * @param events - Array of motion events
 * @param maxItems - Maximum items to return
 * @returns Array of timeline items for UI display
 *
 * @example
 * const timeline = getMotionTimeline(events, 10);
 * // Returns: [
 * //   { id: '1', timestamp: Date, type: 'motion_start', description: 'Motion detected', ... },
 * //   ...
 * // ]
 */
export function getMotionTimeline(
  events: MotionEvent[],
  maxItems: number = 20,
): MotionTimelineItem[] {
  if (!events || events.length === 0) return [];

  const timeline: MotionTimelineItem[] = [];

  // Sort events by timestamp (newest first)
  const sortedEvents = [...events].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  // Take only the requested number of items
  const limitedEvents = sortedEvents.slice(0, maxItems);

  limitedEvents.forEach((event, index) => {
    const item: MotionTimelineItem = {
      id: `motion_${index}_${event.timestamp.getTime()}`,
      timestamp: event.timestamp,
      type: 'motion_start',
      duration: event.duration,
      intensity: event.intensity,
      description: '',
      icon: 'walk',
      color: '#06b6d4', // cyan
    };

    // Determine description and styling based on event characteristics
    if (event.duration < 2) {
      item.description = `Quick motion (${event.duration}s)`;
      item.color = '#f59e0b'; // amber - might be false alarm
      item.icon = 'flash';
    } else if (event.duration > 30) {
      item.description = `Extended activity (${Math.floor(event.duration)}s)`;
      item.color = '#ef4444'; // red - significant activity
      item.icon = 'walk';
    } else {
      item.description = `Motion detected (${Math.floor(event.duration)}s)`;
      item.color = '#06b6d4'; // cyan - normal
      item.icon = 'footsteps';
    }

    // Add intensity info if available
    if (event.intensity !== undefined) {
      item.description += ` • Intensity: ${Math.round(event.intensity)}%`;
    }

    // Add zone info if available
    if (event.zone) {
      item.description += ` • Zone: ${event.zone}`;
    }

    timeline.push(item);
  });

  return timeline;
}

/**
 * Calculate comprehensive motion statistics
 *
 * @param events - Array of motion events
 * @param startDate - Start of analysis period
 * @param endDate - End of analysis period
 * @returns Complete statistics object
 *
 * @example
 * const stats = getMotionStatistics(events, startOfWeek, endOfWeek);
 */
export function getMotionStatistics(
  events: MotionEvent[],
  startDate: Date,
  endDate: Date,
): MotionStats {
  if (!events || events.length === 0) {
    return {
      totalDetections: 0,
      activeHours: 0,
      totalActiveSeconds: 0,
      averageDetectionDuration: 0,
      longestDetection: 0,
      shortestDetection: 0,
      peakHour: 0,
      quietestHour: 0,
      averageIntensity: 0,
      falseAlarmRate: 0,
      confidenceScore: 1,
    };
  }

  // Filter events for the period
  const periodEvents = events.filter((event) => {
    const eventTime = event.timestamp.getTime();
    return eventTime >= startDate.getTime() && eventTime <= endDate.getTime();
  });

  if (periodEvents.length === 0) {
    return {
      totalDetections: 0,
      activeHours: 0,
      totalActiveSeconds: 0,
      averageDetectionDuration: 0,
      longestDetection: 0,
      shortestDetection: 0,
      peakHour: 0,
      quietestHour: 0,
      averageIntensity: 0,
      falseAlarmRate: 0,
      confidenceScore: 1,
    };
  }

  // Calculate basic stats
  const totalDetections = periodEvents.length;
  const activeHours = getActiveHours(periodEvents, startDate, endDate);
  const totalActiveSeconds = periodEvents.reduce((sum, e) => sum + e.duration, 0);
  const averageDetectionDuration = totalActiveSeconds / totalDetections;

  // Find longest and shortest
  const durations = periodEvents.map((e) => e.duration);
  const longestDetection = Math.max(...durations);
  const shortestDetection = Math.min(...durations);

  // Find peak and quietest hours
  const pattern = getMotionPattern(periodEvents, startDate);
  const peakHourData = pattern.reduce((max, hour) =>
    hour.detectionCount > max.detectionCount ? hour : max,
  );
  const quietestHourData = pattern.reduce((min, hour) =>
    hour.detectionCount < min.detectionCount ? hour : min,
  );

  // Calculate average intensity
  const intensities = periodEvents
    .filter((e) => e.intensity !== undefined)
    .map((e) => e.intensity!);
  const averageIntensity =
    intensities.length > 0 ? intensities.reduce((sum, i) => sum + i, 0) / intensities.length : 0;

  // Calculate false alarm rate
  const falseAlarmRate = calculateFalseAlarmRate(periodEvents);

  // Calculate overall confidence score
  const confidences = periodEvents
    .filter((e) => e.confidence !== undefined)
    .map((e) => e.confidence!);
  const confidenceScore =
    confidences.length > 0 ? confidences.reduce((sum, c) => sum + c, 0) / confidences.length : 0.8; // Default 80% if no confidence data

  return {
    totalDetections,
    activeHours,
    totalActiveSeconds,
    averageDetectionDuration,
    longestDetection,
    shortestDetection,
    peakHour: peakHourData.hour,
    quietestHour: quietestHourData.hour,
    averageIntensity,
    falseAlarmRate,
    confidenceScore,
  };
}

/**
 * Get current motion sensor status
 *
 * @param events - Array of all motion events
 * @param currentState - Current sensor state (detecting or not)
 * @param batteryLevel - Current battery level (optional)
 * @param sensitivity - Sensor sensitivity (0-100)
 * @param coverageArea - Coverage area in square meters
 * @returns Complete sensor status
 *
 * @example
 * const status = getMotionSensorStatus(events, true, 85, 75, 5);
 */
export function getMotionSensorStatus(
  events: MotionEvent[],
  currentState: boolean = false,
  batteryLevel?: number,
  sensitivity: number = 50,
  coverageArea: number = 5,
): MotionSensorStatus {
  const now = new Date();
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - 7);

  const monthStart = new Date(now);
  monthStart.setMonth(now.getMonth() - 1);

  // Find last detection
  const sortedEvents = [...events].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  const lastDetection = sortedEvents[0]?.timestamp || null;

  const timeSinceLastDetection = lastDetection
    ? Math.floor((now.getTime() - lastDetection.getTime()) / (1000 * 60))
    : 999999;

  // Count detections
  const detectionCountToday = calculateDetectionCount(events, todayStart, now);
  const detectionCountWeek = calculateDetectionCount(events, weekStart, now);
  const detectionCountMonth = calculateDetectionCount(events, monthStart, now);

  // Calculate active hours today
  const activeHoursToday = getActiveHours(events, todayStart, now);

  // Calculate streaks
  let currentStreak = 0;
  let longestStreak = 0;
  let tempStreak = 0;

  sortedEvents.forEach((event, index) => {
    if (index > 0) {
      const prevEvent = sortedEvents[index - 1];
      const timeDiff = prevEvent.timestamp.getTime() - event.timestamp.getTime();

      // If events are within 10 minutes, consider them part of a streak
      if (timeDiff < 10 * 60 * 1000) {
        tempStreak++;
        if (index === 1) {
          currentStreak = tempStreak;
        }
      } else {
        longestStreak = Math.max(longestStreak, tempStreak);
        tempStreak = 1;
      }
    } else {
      tempStreak = 1;
      currentStreak = 1;
    }
  });

  longestStreak = Math.max(longestStreak, tempStreak);

  return {
    isActive: currentState,
    lastDetection,
    timeSinceLastDetection,
    detectionCountToday,
    detectionCountWeek,
    detectionCountMonth,
    activeHoursToday,
    currentStreak,
    longestStreak,
    batteryLevel,
    sensitivity,
    coverageArea,
  };
}

// ====================================
// Formatting Utilities
// ====================================

/**
 * Format duration in seconds to human-readable string
 *
 * @param seconds - Duration in seconds
 * @param language - 'ar' or 'en'
 * @returns Formatted string
 *
 * @example
 * formatDuration(125, 'en') // "2m 5s"
 * formatDuration(125, 'ar') // "٢ د ٥ ث"
 */
export function formatDuration(seconds: number, language: 'ar' | 'en' = 'en'): string {
  if (seconds < 60) {
    return language === 'ar' ? `${seconds} ث` : `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    return language === 'ar'
      ? `${minutes} د ${remainingSeconds} ث`
      : `${minutes}m ${remainingSeconds}s`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return language === 'ar' ? `${hours} س ${remainingMinutes} د` : `${hours}h ${remainingMinutes}m`;
}

/**
 * Format time since last detection
 *
 * @param minutes - Minutes since last detection
 * @param language - 'ar' or 'en'
 * @returns Formatted string
 *
 * @example
 * formatTimeSince(5, 'en') // "5 minutes ago"
 * formatTimeSince(5, 'ar') // "منذ ٥ دقائق"
 */
export function formatTimeSince(minutes: number, language: 'ar' | 'en' = 'en'): string {
  if (minutes < 1) {
    return language === 'ar' ? 'للتو' : 'Just now';
  }

  if (minutes < 60) {
    return language === 'ar'
      ? `منذ ${minutes} ${minutes === 1 ? 'دقيقة' : 'دقائق'}`
      : `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return language === 'ar'
      ? `منذ ${hours} ${hours === 1 ? 'ساعة' : 'ساعات'}`
      : `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }

  const days = Math.floor(hours / 24);
  return language === 'ar'
    ? `منذ ${days} ${days === 1 ? 'يوم' : 'أيام'}`
    : `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

/**
 * Get detection status label
 *
 * @param count - Detection count
 * @param threshold - Threshold for "high" activity
 * @param language - 'ar' or 'en'
 * @returns Status label
 *
 * @example
 * getDetectionStatus(50, 30, 'en') // "High Activity"
 * getDetectionStatus(50, 30, 'ar') // "نشاط عالي"
 */
export function getDetectionStatus(
  count: number,
  threshold: number = 30,
  language: 'ar' | 'en' = 'en',
): string {
  if (count === 0) {
    return language === 'ar' ? 'لا نشاط' : 'No Activity';
  }

  if (count < threshold / 2) {
    return language === 'ar' ? 'نشاط منخفض' : 'Low Activity';
  }

  if (count < threshold) {
    return language === 'ar' ? 'نشاط متوسط' : 'Moderate Activity';
  }

  return language === 'ar' ? 'نشاط عالي' : 'High Activity';
}

/**
 * Format detection count with label
 *
 * @param count - Detection count
 * @param language - 'ar' or 'en'
 * @returns Formatted string
 *
 * @example
 * formatDetectionCount(47, 'en') // "47 detections"
 * formatDetectionCount(47, 'ar') // "٤٧ كشف"
 */
export function formatDetectionCount(count: number, language: 'ar' | 'en' = 'en'): string {
  if (count === 0) {
    return language === 'ar' ? 'لا كشوفات' : 'No detections';
  }

  if (count === 1) {
    return language === 'ar' ? 'كشف واحد' : '1 detection';
  }

  return language === 'ar' ? `${count} كشف` : `${count} detections`;
}
